import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCircle, Camera, Pencil, Save, Plus, Trash2, Briefcase, Lock, Eye, EyeOff, AlertCircle, CheckCircle, X, Link2, ExternalLink, ArrowUp, ArrowDown, Check } from 'lucide-react';
import { getRoleBadgeClass, getRoleLabel, ProjectInput, prepareImageFile, confirmDialog, alertDialog } from './shared';

export default function ProfileTab({ user, authHeaders, API_URL, isAdmin, projects, refreshUser }) {
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', bio: '', email: '' });
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [localProjects, setLocalProjects] = useState(projects);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', new1: '', new2: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);
  // OTP for email change
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpMaskedEmail, setOtpMaskedEmail] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [pendingSaveData, setPendingSaveData] = useState(null);
  // Private Linkliste
  const [links, setLinks] = useState([]);
  const [linksLoaded, setLinksLoaded] = useState(false);
  const [hearts, setHearts] = useState(null);
  const [linkForm, setLinkForm] = useState({ title: '', url: '' });
  const [linkError, setLinkError] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);
  const [editingLinkIdx, setEditingLinkIdx] = useState(null);
  const [editLinkForm, setEditLinkForm] = useState({ title: '', url: '' });

  useEffect(() => {
    axios.get(`${API_URL}/api/community/profile/links`, { headers: authHeaders })
      .then(r => setLinks(r.data.links || []))
      .catch(() => {})
      .finally(() => setLinksLoaded(true));
    if (user?.id) {
      axios.get(`${API_URL}/api/community/users/${user.id}/card`, { headers: authHeaders })
        .then(r => setHearts(typeof r.data.hearts === 'number' ? r.data.hearts : 0))
        .catch(() => {});
    }
  }, []);

  const saveLinks = async (newLinks) => {
    setLinkSaving(true); setLinkError('');
    try {
      const resp = await axios.put(`${API_URL}/api/community/profile/links`, { links: newLinks }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setLinks(resp.data.links || newLinks);
      setLinkSaving(false);
      return true;
    } catch (err) {
      setLinkError(err.response?.data?.detail || 'Speichern fehlgeschlagen');
      setLinkSaving(false);
      return false;
    }
  };

  const handleAddLink = async () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) { setLinkError('Bitte Titel und URL angeben'); return; }
    if (await saveLinks([...links, { title: linkForm.title.trim(), url: linkForm.url.trim() }])) setLinkForm({ title: '', url: '' });
  };

  const handleMoveLink = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= links.length) return;
    const arr = [...links];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    saveLinks(arr);
  };

  const handleSaveEditLink = async () => {
    if (!editLinkForm.title.trim() || !editLinkForm.url.trim()) { setLinkError('Bitte Titel und URL angeben'); return; }
    const arr = links.map((l, i) => i === editingLinkIdx ? { title: editLinkForm.title.trim(), url: editLinkForm.url.trim() } : l);
    if (await saveLinks(arr)) setEditingLinkIdx(null);
  };

  const pwStrength = (() => {
    const pw = pwForm.new1;
    const checks = [
      { ok: pw.length >= 8, label: 'Min. 8 Zeichen' },
      { ok: /[A-Z]/.test(pw), label: 'Großbuchstabe' },
      { ok: /[a-z]/.test(pw), label: 'Kleinbuchstabe' },
      { ok: /[0-9]/.test(pw), label: 'Zahl' },
      { ok: /[!@#$%^&*(),.?":{}|<>]/.test(pw), label: 'Sonderzeichen' },
    ];
    return { checks, score: checks.filter(c => c.ok).length };
  })();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (pwForm.new1 !== pwForm.new2) { setPwError('Neue Passwörter stimmen nicht überein'); return; }
    if (pwStrength.score < 5) { setPwError('Bitte erfülle alle Passwort-Anforderungen'); return; }
    setPwSaving(true);
    try {
      const hdrs = { ...authHeaders, 'Content-Type': 'application/json' };
      await axios.put(`${API_URL}/api/community/profile/password`, { current_password: pwForm.current, new_password: pwForm.new1 }, { headers: hdrs });
      setPwSuccess('Passwort erfolgreich geändert!');
      setPwForm({ current: '', new1: '', new2: '' });
      setTimeout(() => { setPwSuccess(''); setShowPasswordChange(false); }, 2000);
    } catch (err) { setPwError(err.response?.data?.detail || 'Fehler beim Ändern'); }
    setPwSaving(false);
  };

  const startEditProfile = () => {
    const bd = user?.birthday || '';
    const parts = bd.split('-');
    setProfileForm({
      first_name: user?.first_name || '', last_name: user?.last_name || '',
      bio: user?.bio || '', email: user?.email || '',
      birthday_month: parts[0] ? String(parseInt(parts[0])) : '', birthday_day: parts[1] ? String(parseInt(parts[1])) : '',
    });
    setProfileEditing(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const birthday = profileForm.birthday_month && profileForm.birthday_day
      ? `${profileForm.birthday_month.padStart(2, '0')}-${profileForm.birthday_day.padStart(2, '0')}`
      : '';
    const saveData = { ...profileForm, birthday, birthday_month: undefined, birthday_day: undefined };

    // Check if email changed
    const newEmail = (profileForm.email || '').trim().toLowerCase();
    const oldEmail = (user?.email || '').trim().toLowerCase();
    if (newEmail && newEmail !== oldEmail) {
      // Email changed → request OTP first
      setPendingSaveData(saveData);
      setOtpSending(true);
      setOtpError('');
      try {
        const resp = await axios.post(`${API_URL}/api/community/profile/request-email-otp`, {}, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
        setOtpMaskedEmail(resp.data.masked_email || '');
        setShowOtpModal(true);
        setOtpCode('');
      } catch (err) { setOtpError(err.response?.data?.detail || 'Fehler beim Senden des OTP'); }
      setOtpSending(false);
      return;
    }

    // No email change → save directly
    setProfileSaving(true);
    try {
      await axios.put(`${API_URL}/api/community/profile`, saveData, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setProfileEditing(false);
      if (refreshUser) await refreshUser();
    } catch (err) { console.error(err); }
    setProfileSaving(false);
  };

  const handleOtpConfirm = async () => {
    if (!otpCode.trim() || !pendingSaveData) return;
    setOtpSending(true);
    setOtpError('');
    try {
      await axios.put(`${API_URL}/api/community/profile`, { ...pendingSaveData, email_otp: otpCode.trim() }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setShowOtpModal(false);
      setPendingSaveData(null);
      setProfileEditing(false);
      if (refreshUser) await refreshUser();
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Fehler bei der Verifizierung');
    }
    setOtpSending(false);
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    const input = e.target;
    if (!file) return;
    let toUpload;
    try { toUpload = await prepareImageFile(file); } catch (err) { alertDialog(err.message, { title: 'Bild nicht geeignet', danger: true }); input.value = ''; return; }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', toUpload);
      await axios.post(`${API_URL}/api/community/profile/image`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (refreshUser) await refreshUser();
    } catch (e) { alertDialog(e.response?.data?.detail || 'Upload fehlgeschlagen', { title: 'Upload fehlgeschlagen', danger: true }); }
    setUploadingImage(false);
    input.value = '';
  };

  return (
    <div className="tab-content" data-testid="profil-content">
      <div className="profile-container">
        <div className="profile-card">
          <div className="profile-image-section">
            <div className="profile-avatar">
              {user.profile_image ? (
                <img src={`${API_URL}${user.profile_image}`} alt="Profilbild" className="profile-avatar-img" />
              ) : (
                <UserCircle size={64} className="profile-avatar-placeholder" />
              )}
              <label className="profile-upload-btn" data-testid="upload-image-btn">
                <Camera size={16} />
                <input type="file" accept="image/*" onChange={handleUploadImage} hidden data-testid="profile-image-input" />
                {uploadingImage ? 'Laden...' : 'Bild ändern'}
              </label>
            </div>
            <div className="profile-name-section">
              <h2>{user.first_name} {user.last_name}</h2>
              <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>{getRoleLabel(user.role)}</span>
              {hearts !== null && <p className="profile-hearts" title="Erhaltene Herzen auf Beiträge und Kommentare" data-testid="profile-hearts">❤️ {hearts}</p>}
              <p className="profile-email">{user.email}</p>
              <p className="profile-username">{user.username}</p>
            </div>
          </div>

          {!profileEditing ? (
            <div className="profile-details">
              <div className="profile-field"><span className="profile-label">Vorname</span><span className="profile-value">{user.first_name}</span></div>
              <div className="profile-field"><span className="profile-label">Nachname</span><span className="profile-value">{user.last_name}</span></div>
              <div className="profile-field"><span className="profile-label">E-Mail</span><span className="profile-value">{user.email}</span></div>
              <div className="profile-field"><span className="profile-label">Benutzername</span><span className="profile-value">{user.username}</span></div>
              <div className="profile-field full"><span className="profile-label">Bio</span><span className="profile-value">{user.bio || 'Noch keine Bio'}</span></div>
              <div className="profile-field"><span className="profile-label">Geburtstag</span><span className="profile-value">{user.birthday ? (() => { const [m, d] = user.birthday.split('-'); const months = ['','Januar','Februar','Maerz','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember']; return `${parseInt(d)}. ${months[parseInt(m)] || m}`; })() : 'Nicht angegeben'}</span></div>
              {localProjects.length > 0 && (
                <div className="profile-field full">
                  <span className="profile-label">Projekte</span>
                  <div className="profile-projects-display">
                    {localProjects.map(p => {
                      const pid = (user.project_ids || {})[p.id];
                      return <span key={p.id} className="project-display-item"><strong>{p.name}:</strong> {pid || '–'}</span>;
                    })}
                  </div>
                </div>
              )}
              <button onClick={startEditProfile} className="btn-sm" data-testid="edit-profile-btn"><Pencil size={14} /> Profil bearbeiten</button>
              <button onClick={() => { setShowPasswordChange(!showPasswordChange); setPwError(''); setPwSuccess(''); setPwForm({ current: '', new1: '', new2: '' }); }} className="btn-sm" style={{ marginLeft: '0.5rem', background: 'transparent', border: '1px solid rgba(57,255,20,0.3)', color: '#C8A45C' }} data-testid="change-password-btn"><Lock size={14} /> Passwort ändern</button>

              {showPasswordChange && (
                <form onSubmit={handleChangePassword} className="profile-edit-form" style={{ marginTop: '1rem', padding: '1rem', background: '#0A0E27', borderRadius: '10px', border: '1px solid rgba(57,255,20,0.15)' }}>
                  <h4 style={{ color: '#C8A45C', margin: '0 0 0.75rem', fontSize: '0.9rem' }}>Passwort ändern</h4>
                  {pwError && <div className="form-error" style={{ marginBottom: '0.75rem' }}><AlertCircle size={14} /><span>{pwError}</span></div>}
                  {pwSuccess && <div style={{ color: '#C8A45C', fontSize: '0.82rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><CheckCircle size={14} />{pwSuccess}</div>}
                  <div className="form-group">
                    <label>Aktuelles Passwort</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPwCurrent ? 'text' : 'password'} value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})} required data-testid="pw-current" />
                      <button type="button" onClick={() => setShowPwCurrent(!showPwCurrent)} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#8C92AC', cursor: 'pointer' }}>{showPwCurrent ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Neues Passwort</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPwNew ? 'text' : 'password'} value={pwForm.new1} onChange={e => setPwForm({...pwForm, new1: e.target.value})} required data-testid="pw-new" />
                      <button type="button" onClick={() => setShowPwNew(!showPwNew)} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#8C92AC', cursor: 'pointer' }}>{showPwNew ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' }}>
                      {pwStrength.checks.map((c, i) => (
                        <span key={`pw-${c.label}`} style={{ fontSize: '0.68rem', padding: '0.1rem 0.4rem', borderRadius: '4px', background: c.ok ? 'rgba(57,255,20,0.12)' : 'rgba(255,0,85,0.1)', color: c.ok ? '#C8A45C' : '#FF0055' }}>{c.ok ? '\u2713' : '\u2717'} {c.label}</span>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Neues Passwort bestätigen</label>
                    <input type="password" value={pwForm.new2} onChange={e => setPwForm({...pwForm, new2: e.target.value})} required data-testid="pw-confirm" />
                    {pwForm.new2 && pwForm.new1 !== pwForm.new2 && <span style={{ fontSize: '0.72rem', color: '#FF0055', marginTop: '0.2rem' }}>Passwörter stimmen nicht überein</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button type="submit" className="btn-sm" disabled={pwSaving || pwStrength.score < 5 || pwForm.new1 !== pwForm.new2} data-testid="pw-submit"><Save size={14} /> {pwSaving ? 'Speichern...' : 'Passwort ändern'}</button>
                    <button type="button" className="btn-icon" onClick={() => setShowPasswordChange(false)}>Abbrechen</button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={handleSaveProfile} className="profile-edit-form">
              <div className="form-row">
                <div className="form-group"><label>Vorname</label><input value={profileForm.first_name} onChange={e => setProfileForm({...profileForm, first_name: e.target.value})} data-testid="profile-firstname" /></div>
                <div className="form-group"><label>Nachname</label><input value={profileForm.last_name} onChange={e => setProfileForm({...profileForm, last_name: e.target.value})} data-testid="profile-lastname" /></div>
              </div>
              <div className="form-group"><label>E-Mail</label><input type="email" value={profileForm.email} onChange={e => setProfileForm({...profileForm, email: e.target.value})} data-testid="profile-email" /></div>
              <div className="form-group"><label>Bio</label><textarea value={profileForm.bio} onChange={e => setProfileForm({...profileForm, bio: e.target.value})} rows={4} placeholder="Erzähl etwas über dich..." data-testid="profile-bio" /></div>
              <div className="form-group">
                <label>Geburtstag (Tag &amp; Monat)</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <select value={profileForm.birthday_day || ''} onChange={e => setProfileForm({...profileForm, birthday_day: e.target.value})} data-testid="profile-birthday-day" style={{ flex: 1 }}>
                    <option value="">Tag</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={String(d)}>{d}</option>
                    ))}
                  </select>
                  <select value={profileForm.birthday_month || ''} onChange={e => setProfileForm({...profileForm, birthday_month: e.target.value})} data-testid="profile-birthday-month" style={{ flex: 1 }}>
                    <option value="">Monat</option>
                    {['Januar','Februar','Maerz','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'].map((m, i) => (
                      <option key={i+1} value={String(i+1)}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              {localProjects.length > 0 && (
                <div className="form-group">
                  <label>Projekt User-IDs</label>
                  <div className="projects-grid">
                    {localProjects.map(p => (
                      <ProjectInput key={p.id} project={p} initialValue={(user.project_ids || {})[p.id] || ''} apiUrl={API_URL} authHeaders={authHeaders} />
                    ))}
                  </div>
                </div>
              )}
              <div className="profile-edit-actions">
                <button type="submit" className="btn-sm" disabled={profileSaving} data-testid="save-profile-btn"><Save size={14} /> {profileSaving ? 'Speichern...' : 'Speichern'}</button>
                <button type="button" className="btn-icon" onClick={() => setProfileEditing(false)}>Abbrechen</button>
              </div>
            </form>
          )}
        </div>

        {/* Private Linkliste */}
        <div className="section-card linklist-card" style={{marginTop: '1.5rem'}} data-testid="linklist-section">
          <h3><Link2 size={18} /> Meine Linkliste</h3>
          <p className="linklist-hint">Nur für dich sichtbar – speichere hier deine wichtigsten Links.</p>
          {linkError && <div className="form-error" style={{ marginBottom: '0.6rem' }}><AlertCircle size={14} /><span>{linkError}</span></div>}
          {links.length === 0 ? (
            linksLoaded && <p className="linklist-empty" data-testid="linklist-empty">Noch keine Links gespeichert.</p>
          ) : (
            <div className="linklist-items">
              {links.map((l, idx) => (
                <div key={`link-${idx}-${l.url}`} className="linklist-item" data-testid={`linklist-item-${idx}`}>
                  {editingLinkIdx === idx ? (
                    <div className="linklist-edit-row">
                      <input value={editLinkForm.title} onChange={e => setEditLinkForm({...editLinkForm, title: e.target.value})} placeholder="Titel" maxLength={100} data-testid="linklist-edit-title" />
                      <input value={editLinkForm.url} onChange={e => setEditLinkForm({...editLinkForm, url: e.target.value})} placeholder="https://..." data-testid="linklist-edit-url" onKeyDown={e => { if (e.key === 'Enter') handleSaveEditLink(); }} />
                      <div className="linklist-actions">
                        <button type="button" className="btn-icon-sm" onClick={handleSaveEditLink} disabled={linkSaving} title="Speichern" data-testid="linklist-edit-save"><Check size={14} /></button>
                        <button type="button" className="btn-icon-sm" onClick={() => setEditingLinkIdx(null)} title="Abbrechen" data-testid="linklist-edit-cancel"><X size={14} /></button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <a href={l.url} target="_blank" rel="noopener noreferrer" className="linklist-link" data-testid={`linklist-link-${idx}`}>
                        <ExternalLink size={13} /> <span>{l.title}</span>
                      </a>
                      <div className="linklist-actions">
                        <button type="button" className="btn-icon-sm" onClick={() => handleMoveLink(idx, -1)} disabled={idx === 0 || linkSaving} title="Nach oben" data-testid={`linklist-up-${idx}`}><ArrowUp size={13} /></button>
                        <button type="button" className="btn-icon-sm" onClick={() => handleMoveLink(idx, 1)} disabled={idx === links.length - 1 || linkSaving} title="Nach unten" data-testid={`linklist-down-${idx}`}><ArrowDown size={13} /></button>
                        <button type="button" className="btn-icon-sm" onClick={() => { setEditingLinkIdx(idx); setEditLinkForm({ title: l.title, url: l.url }); setLinkError(''); }} disabled={linkSaving} title="Bearbeiten" data-testid={`linklist-edit-${idx}`}><Pencil size={13} /></button>
                        <button type="button" className="btn-icon-sm danger" onClick={async () => { if (await confirmDialog(`Link "${l.title}" löschen?`, { title: 'Link löschen' })) saveLinks(links.filter((_, i) => i !== idx)); }} disabled={linkSaving} title="Löschen" data-testid={`linklist-delete-${idx}`}><Trash2 size={13} /></button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="linklist-add-row">
            <input value={linkForm.title} onChange={e => setLinkForm({...linkForm, title: e.target.value})} placeholder="Titel" maxLength={100} data-testid="linklist-title-input" onKeyDown={e => { if (e.key === 'Enter') handleAddLink(); }} />
            <input value={linkForm.url} onChange={e => setLinkForm({...linkForm, url: e.target.value})} placeholder="https://..." data-testid="linklist-url-input" onKeyDown={e => { if (e.key === 'Enter') handleAddLink(); }} />
            <button type="button" className="btn-sm" onClick={handleAddLink} disabled={linkSaving} data-testid="linklist-add-btn"><Plus size={14} /> Link</button>
          </div>
        </div>

        {/* Admin: Projekt-Verwaltung */}
        {isAdmin && (
          <div className="section-card" style={{marginTop: '1.5rem'}}>
            <h3><Briefcase size={18} /> Projekte verwalten</h3>
            {localProjects.length > 0 && (
              <div className="projects-admin-list">
                {localProjects.map(p => (
                  <div key={p.id} className="project-admin-item" data-testid={`project-item-${p.id}`}>
                    <span>{p.name}</span>
                    <button className="btn-icon-sm danger" data-testid={`delete-project-${p.id}`} onClick={async () => { if (await confirmDialog(`Projekt "${p.name}" löschen? Alle User-IDs gehen verloren.`, { title: 'Projekt löschen' })) { try { await axios.delete(`${API_URL}/api/community/projects/${p.id}`, { headers: authHeaders }); const resp = await axios.get(`${API_URL}/api/community/projects`, { headers: authHeaders }); setLocalProjects(resp.data.projects || []); } catch(err) { alertDialog(err.response?.data?.detail || 'Löschen fehlgeschlagen', { title: 'Fehler', danger: true }); } }}}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            <div style={{marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
              <input id="new-project-name" data-testid="new-project-input" className="project-input" placeholder="Neues Projekt..." style={{flex: 1}} />
              <button className="btn-sm" onClick={async () => {
                const input = document.getElementById('new-project-name');
                if (input.value.trim()) {
                  try {
                    await axios.post(`${API_URL}/api/community/projects`, { name: input.value.trim() }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
                    input.value = '';
                    const resp = await axios.get(`${API_URL}/api/community/projects`, { headers: authHeaders });
                    setLocalProjects(resp.data.projects || []);
                  } catch(err) {}
                }
              }} data-testid="add-project-btn"><Plus size={14} /> Projekt</button>
            </div>
          </div>
        )}
      </div>

      {/* OTP Modal for email change */}
      {showOtpModal && (
        <div className="modal-overlay" onClick={() => setShowOtpModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3>E-Mail-Änderung bestätigen</h3>
              <button className="modal-close" onClick={() => setShowOtpModal(false)} data-testid="otp-modal-close"><X size={18} /></button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <p style={{ color: '#B8B8B8', fontSize: '0.88rem', marginBottom: '1rem' }}>
                Ein 6-stelliger Sicherheitscode wurde an <strong style={{ color: '#C8A45C' }}>{otpMaskedEmail}</strong> gesendet.
              </p>
              {otpError && <div className="form-error" style={{ marginBottom: '0.75rem' }}><AlertCircle size={14} /><span>{otpError}</span></div>}
              <div className="form-group">
                <label>Sicherheitscode</label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-stelliger Code"
                  maxLength={6}
                  autoFocus
                  style={{ textAlign: 'center', fontSize: '1.3rem', letterSpacing: '6px', fontWeight: 'bold' }}
                  onKeyDown={e => { if (e.key === 'Enter' && otpCode.length === 6) handleOtpConfirm(); }}
                  data-testid="otp-input"
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button className="btn-login" onClick={handleOtpConfirm} disabled={otpCode.length !== 6 || otpSending} data-testid="otp-confirm" style={{ flex: 1 }}>
                  {otpSending ? 'Prüfen...' : 'Bestätigen'}
                </button>
                <button className="btn-icon" onClick={() => setShowOtpModal(false)} style={{ padding: '0.5rem 1rem' }}>Abbrechen</button>
              </div>
              <p style={{ color: '#8C92AC', fontSize: '0.72rem', marginTop: '0.75rem', textAlign: 'center' }}>Der Code ist 10 Minuten gültig.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
